import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Megaphone, Plus, Trash2, Pencil, Save, X, CheckCircle, Clock, Calendar, AlertTriangle } from 'lucide-react';
import { neonQuery } from '../services/api';

// ───────────────────────────────────────────────────────────────────────────
// Promo Banner scheduler (Configure Site → Promo Banner).
//
// Controls the orange promo bar at the top of every storefront page (the bar
// shown to non-VIP shoppers). One DEFAULT banner + any number of date-ranged
// SCHEDULED overrides for holidays / special periods. Schedule entries can be
// set for any time in the future, including out-years — so we never risk a
// "Father's Day" message lingering past Father's Day.
//
// The storefront reads the resolved active banner from the public
// site-banner.js function. This page is the admin CRUD (neon.js site_banner_*).
// Overlap rule (matches the server): an enabled entry covering today wins;
// on overlap the latest start_date wins, then the highest id.
// ───────────────────────────────────────────────────────────────────────────

const ORANGE = '#DC582A';
const NAVY = '#00395C';

const INPUT = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
};
const LABEL = { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 };

// Today's date as 'YYYY-MM-DD' in America/New_York — must match the server so
// the "Showing now" badge agrees with what the storefront actually renders.
function todayET() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

// Parse a 'YYYY-MM-DD' string as a LOCAL date and format it for display
// (avoids the UTC-midnight one-day-early bug).
function fmtDate(s) {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y) return s;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusOf(entry, today) {
  if (!entry.enabled) return 'Disabled';
  if (today < entry.start_date) return 'Upcoming';
  if (today > entry.end_date) return 'Expired';
  return 'Active';
}

const STATUS_STYLE = {
  Active: { bg: '#e7f5ec', fg: '#1a7f3c', icon: CheckCircle },
  Upcoming: { bg: '#eef3fa', fg: NAVY, icon: Clock },
  Expired: { bg: '#f1f1f1', fg: '#888', icon: Calendar },
  Disabled: { bg: '#fbecea', fg: '#b3401f', icon: X },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Expired;
  const Icon = s.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 999, fontSize: 11, fontWeight: 700, background: s.bg, color: s.fg,
    }}>
      <Icon size={12} /> {status}
    </span>
  );
}

// The actual orange bar, rendered exactly like the storefront so Greg sees what
// shoppers see before saving.
function BannerPreview({ title, subtext, disabled }) {
  if (disabled || (!title && !subtext)) {
    return (
      <div style={{
        background: '#f1f1f1', color: '#888', textAlign: 'center', padding: '10px 20px',
        fontSize: 13, fontStyle: 'italic', borderRadius: 6,
      }}>
        No banner shown
      </div>
    );
  }
  return (
    <div style={{
      background: ORANGE, color: '#fff', textAlign: 'center', padding: '10px 20px',
      fontSize: 14, lineHeight: 1.3, fontWeight: 600, borderRadius: 6,
    }}>
      <span>{title}</span>
      {subtext ? <span style={{ marginLeft: 6 }}>{subtext}</span> : null}
    </div>
  );
}

const EMPTY_ENTRY = { id: null, label: '', title: '', subtext: '', start_date: '', end_date: '', enabled: true };

export default function BannerScheduler() {
  const today = useMemo(() => todayET(), []);
  const [config, setConfig] = useState({ title: '', subtext: '', enabled: true });
  const [configDirty, setConfigDirty] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // null | EMPTY_ENTRY-shaped object

  const flash = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, listRes] = await Promise.all([
        neonQuery('site_banner_config_get'),
        neonQuery('site_banner_list'),
      ]);
      const cfg = (cfgRes && cfgRes.config) || {};
      setConfig({
        title: cfg.title || '',
        subtext: cfg.subtext || '',
        enabled: cfg.enabled !== false,
      });
      setConfigDirty(false);
      setEntries(Array.isArray(listRes) ? listRes.map(e => ({ ...e, enabled: e.enabled !== false })) : []);
    } catch (e) {
      flash('Could not load banners: ' + e.message, false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Which banner is showing on the storefront right now (mirrors the server).
  const showingNow = useMemo(() => {
    const active = entries
      .filter(e => statusOf(e, today) === 'Active')
      .sort((a, b) => (a.start_date < b.start_date ? 1 : a.start_date > b.start_date ? -1 : b.id - a.id));
    if (active.length) return { source: 'scheduled', entry: active[0] };
    if (config.enabled && (config.title || config.subtext)) return { source: 'default' };
    return { source: 'none' };
  }, [entries, config, today]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await neonQuery('site_banner_config_set', { config });
      setConfigDirty(false);
      flash('Default banner saved.');
    } catch (e) {
      flash('Save failed: ' + e.message, false);
    } finally {
      setSaving(false);
    }
  };

  const saveEntry = async () => {
    if (!editing) return;
    if (!editing.start_date || !editing.end_date) { flash('Pick a start and end date.', false); return; }
    if (editing.end_date < editing.start_date) { flash('End date must be on or after the start date.', false); return; }
    if (!editing.title && !editing.subtext) { flash('Enter banner text.', false); return; }
    setSaving(true);
    try {
      await neonQuery('site_banner_upsert', editing);
      setEditing(null);
      await load();
      flash(editing.id ? 'Scheduled banner updated.' : 'Scheduled banner added.');
    } catch (e) {
      flash('Save failed: ' + e.message, false);
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm('Delete this scheduled banner?')) return;
    try {
      await neonQuery('site_banner_delete', { id });
      setEntries(es => es.filter(e => e.id !== id));
      flash('Deleted.');
    } catch (e) {
      flash('Delete failed: ' + e.message, false);
    }
  };

  const toggleEntry = async (entry) => {
    try {
      await neonQuery('site_banner_upsert', { ...entry, enabled: !entry.enabled });
      setEntries(es => es.map(e => e.id === entry.id ? { ...e, enabled: !e.enabled } : e));
    } catch (e) {
      flash('Update failed: ' + e.message, false);
    }
  };

  // Overlap warning: two enabled entries whose date ranges intersect.
  const overlaps = useMemo(() => {
    const en = entries.filter(e => e.enabled);
    for (let i = 0; i < en.length; i++) {
      for (let j = i + 1; j < en.length; j++) {
        if (en[i].start_date <= en[j].end_date && en[j].start_date <= en[i].end_date) return true;
      }
    }
    return false;
  }, [entries]);

  const sorted = useMemo(() => {
    const rank = { Active: 0, Upcoming: 1, Disabled: 2, Expired: 3 };
    return [...entries].sort((a, b) => {
      const ra = rank[statusOf(a, today)], rb = rank[statusOf(b, today)];
      if (ra !== rb) return ra - rb;
      return a.start_date < b.start_date ? 1 : a.start_date > b.start_date ? -1 : 0;
    });
  }, [entries, today]);

  return (
    <div style={{ padding: '0 8px', maxWidth: 980 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Megaphone size={20} color="var(--orange)" />
        <h2 style={{ margin: 0, color: 'var(--navy)', fontSize: 18 }}>Promo Banner</h2>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--gray-500)', maxWidth: 720 }}>
        The orange bar at the top of every storefront page (shown to non-VIP shoppers).
        Set the everyday default below, then schedule holiday or special messages for
        any date range. Scheduled messages turn on and off on their own, so nothing
        ever lingers past its date.
      </p>

      {/* Showing-now strip */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
          Showing on the store right now
        </div>
        {showingNow.source === 'scheduled' && (
          <>
            <BannerPreview title={showingNow.entry.title} subtext={showingNow.entry.subtext} />
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
              Scheduled: <strong>{showingNow.entry.label || 'Untitled'}</strong> (through {fmtDate(showingNow.entry.end_date)})
            </div>
          </>
        )}
        {showingNow.source === 'default' && (
          <>
            <BannerPreview title={config.title} subtext={config.subtext} />
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Default banner (nothing scheduled today)</div>
          </>
        )}
        {showingNow.source === 'none' && <BannerPreview disabled />}
      </div>

      {/* Default banner */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>Default banner</div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
            Shown whenever no special message is scheduled for the day.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LABEL}>Headline</label>
              <input style={INPUT} value={config.title}
                placeholder="Fresh Maryland seafood, delivered to your door."
                onChange={e => { setConfig(c => ({ ...c, title: e.target.value })); setConfigDirty(true); }} />
            </div>
            <div>
              <label style={LABEL}>Second line (optional)</label>
              <input style={INPUT} value={config.subtext}
                placeholder="Order by 4pm ET for fast shipping."
                onChange={e => { setConfig(c => ({ ...c, subtext: e.target.value })); setConfigDirty(true); }} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.enabled}
              onChange={e => { setConfig(c => ({ ...c, enabled: e.target.checked })); setConfigDirty(true); }} />
            Show the banner when nothing is scheduled (uncheck to hide the bar entirely on normal days)
          </label>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 4 }}>Preview</div>
            <BannerPreview title={config.title} subtext={config.subtext} disabled={!config.enabled} />
          </div>
          <button className="btn btn-primary" disabled={!configDirty || saving} onClick={saveConfig}
            style={{ opacity: (!configDirty || saving) ? 0.5 : 1 }}>
            <Save size={14} style={{ marginRight: 6 }} /> Save default
          </button>
        </div>
      </div>

      {/* Scheduled banners */}
      <div className="card">
        <div className="card-header" style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Scheduled banners</span>
          <button className="btn btn-primary" style={{ padding: '5px 10px', fontSize: 12 }}
            onClick={() => setEditing({ ...EMPTY_ENTRY })}>
            <Plus size={13} style={{ marginRight: 4 }} /> Add scheduled banner
          </button>
        </div>
        <div className="card-body">
          {overlaps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff6e6', border: '1px solid #f0d9a0',
              borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#8a6d1f', marginBottom: 12 }}>
              <AlertTriangle size={14} /> Two or more scheduled banners overlap. On those days, the one that starts latest will show.
            </div>
          )}

          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--gray-500)', padding: 12 }}>Loading…</div>
          ) : sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--gray-500)', padding: 12 }}>
              No scheduled banners yet. Add one for an upcoming holiday or sale.
            </div>
          ) : (
            <table className="orders-table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Name</th>
                  <th style={{ textAlign: 'left' }}>Message</th>
                  <th style={{ textAlign: 'left' }}>Dates</th>
                  <th style={{ textAlign: 'left' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(e => {
                  const status = statusOf(e, today);
                  return (
                    <tr key={e.id} style={{ opacity: status === 'Expired' ? 0.6 : 1 }}>
                      <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{e.label || <span style={{ color: 'var(--gray-400)' }}>Untitled</span>}</td>
                      <td style={{ maxWidth: 280 }}>
                        <div>{e.title}</div>
                        {e.subtext ? <div style={{ color: 'var(--gray-500)' }}>{e.subtext}</div> : null}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.start_date)}<br />→ {fmtDate(e.end_date)}</td>
                      <td><StatusPill status={status} /></td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button title={e.enabled ? 'Disable' : 'Enable'} onClick={() => toggleEntry(e)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: e.enabled ? '#1a7f3c' : '#b3401f', marginRight: 6, fontSize: 11, fontWeight: 700 }}>
                          {e.enabled ? 'On' : 'Off'}
                        </button>
                        <button title="Edit" onClick={() => setEditing({ ...e })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', marginRight: 4 }}>
                          <Pencil size={14} />
                        </button>
                        <button title="Delete" onClick={() => removeEntry(e.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b3401f' }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit / add modal */}
      {editing && (
        <div onClick={() => setEditing(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={ev => ev.stopPropagation()} style={{
            background: '#fff', borderRadius: 10, width: '100%', maxWidth: 560, maxHeight: '90vh',
            overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ color: 'var(--navy)' }}>{editing.id ? 'Edit scheduled banner' : 'New scheduled banner'}</strong>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Name (for your reference only)</label>
                <input style={INPUT} value={editing.label} placeholder="e.g. Father's Day 2026"
                  onChange={e => setEditing(s => ({ ...s, label: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Headline</label>
                <input style={INPUT} value={editing.title} placeholder="Father's Day is June 21st."
                  onChange={e => setEditing(s => ({ ...s, title: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Second line (optional)</label>
                <input style={INPUT} value={editing.subtext} placeholder="Get Dad something amazing this year!"
                  onChange={e => setEditing(s => ({ ...s, subtext: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={LABEL}>Start date</label>
                  <input type="date" style={INPUT} value={editing.start_date || ''}
                    onChange={e => setEditing(s => ({ ...s, start_date: e.target.value }))} />
                </div>
                <div>
                  <label style={LABEL}>End date (last day shown)</label>
                  <input type="date" style={INPUT} value={editing.end_date || ''}
                    onChange={e => setEditing(s => ({ ...s, end_date: e.target.value }))} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={editing.enabled}
                  onChange={e => setEditing(s => ({ ...s, enabled: e.target.checked }))} />
                Enabled (uncheck to keep it scheduled but turned off)
              </label>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 4 }}>Preview</div>
                <BannerPreview title={editing.title} subtext={editing.subtext} disabled={!editing.enabled} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={saving} onClick={saveEntry}>
                  <Save size={14} style={{ marginRight: 6 }} /> {editing.id ? 'Save changes' : 'Add banner'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1100, padding: '10px 16px', borderRadius: 8,
          background: toast.ok ? '#1a7f3c' : '#b3401f', color: '#fff', fontSize: 13, fontWeight: 600,
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
